import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Tab, Tabs, Theme } from '@material-ui/core'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import clsx from 'clsx'
import { SPEC_INT_AI_COLOR } from 'components/SpecIntelligence/specIntelligenceConstants'
import React from 'react'
import AIIcon from 'resources/AIIcon'

/**
 * The sub-tabs available on the project Reports page.
 *
 * String-valued rather than index-based so tests and markup read clearly, and so
 * the values can be promoted to URL segments if these sub-tabs ever become
 * nested routes.
 */
export enum ReportsSubTab {
    Reports = 'reports',
    Insights = 'insights',
}

const useStyles = makeStyles((theme: Theme) => ({
    tabRoot: {
        minWidth: pxToRem(70),
        color: '#04325B',
        opacity: 1,
        textTransform: 'none',
    },
    tabLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    /**
     * Sized with both `fontSize` and explicit dimensions so it applies to a MUI
     * `SvgIcon` (which scales off `fontSize`) and to `AIIcon` (a raw SVG with
     * `width`/`height` attributes) alike.
     */
    tabIcon: {
        fontSize: pxToRem(18),
        width: pxToRem(18),
        height: pxToRem(18),
    },
    /**
     * Keeps the AI sparkle on-brand instead of inheriting the tab's text color,
     * so it stays purple whether or not the Insights tab is selected.
     */
    aiTabIcon: {
        color: SPEC_INT_AI_COLOR,
    },
    tabSelected: {
        color: '#A78966',
    },
    indicator: {
        height: pxToRem(4),
    },
    tabs: {
        paddingLeft: theme.spacing(1.5),
    },
}))

/** Builds the ARIA wiring and test id for a single sub-tab. */
const a11yProps = (value: ReportsSubTab) => ({
    id: `reportsSubTab-${value}`,
    'aria-controls': `reportsSubTabPanel-${value}`,
    'data-testid': `${value}SubTab`,
})

/** Props for {@link ReportsSubTabNav}. */
export interface ReportsSubTabNavProps {
    /** The currently active sub-tab. */
    value: ReportsSubTab
    /** Called with the newly selected sub-tab. */
    onChange: (tab: ReportsSubTab) => void
}

/**
 * Tab row that switches the project Reports page between its "Reports" and
 * "Insights" panels.
 *
 * Purely presentational — the active tab is owned by the parent page so that the
 * panels themselves can stay mounted across switches.
 */
const ReportsSubTabNav = ({ value, onChange }: ReportsSubTabNavProps) => {
    const classes = useStyles()

    const handleChange = (
        _event: React.ChangeEvent<{}>,
        newValue: ReportsSubTab
    ) => {
        onChange(newValue)
    }

    return (
        <Tabs
            value={value}
            onChange={handleChange}
            className={classes.tabs}
            classes={{ indicator: classes.indicator }}
            data-testid="reportsSubTabNav"
        >
            <Tab
                label={
                    <span className={classes.tabLabel}>
                        <DescriptionOutlinedIcon
                            className={classes.tabIcon}
                            aria-hidden="true"
                        />
                        Reports
                    </span>
                }
                value={ReportsSubTab.Reports}
                {...a11yProps(ReportsSubTab.Reports)}
                classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
            />
            <Tab
                label={
                    <span className={classes.tabLabel}>
                        <AIIcon
                            className={clsx(classes.tabIcon, classes.aiTabIcon)}
                        />
                        Insights
                    </span>
                }
                value={ReportsSubTab.Insights}
                {...a11yProps(ReportsSubTab.Insights)}
                classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
            />
        </Tabs>
    )
}

export default ReportsSubTabNav
